import type { Bridge } from '@casa/bridge';
import { createPermissionGate, type DataSource, type DriverPermissions } from '@casa/driver-sdk';
import type { DriverEvents, DriverLink } from '@casa/runtime';
import { createHostCalls } from './host_calls.ts';
import type { CallReply, DriverAnswers, DriverCode, DriverQuestion, PageMessage, WorkerLike, WorkerMessage } from '../messages.ts';

/**
 * The driver host (plan step 1.5): one driver instance in its own Worker, as a `DriverLink` for
 * `@casa/runtime`. The Worker comes from `/sandbox/`, where every host serves it with
 * `default-src 'none'` (it cannot reach the network by itself); its `Host` calls come here and
 * pass the permission gate before they reach the bridge.
 */

export interface DriverHostOptions {
    bridge: Bridge;
    /** Creates the Worker; the app's comes from `/sandbox/`, tests run one in-process. */
    createWorker: (name: string) => WorkerLike;
    /** The connection's name in the dashboard. */
    connection: string;
    driver: DriverCode;
    /** For built-in drivers their manifest's; for installed ones those the user consented to. */
    permissions: DriverPermissions;
    /** The connection's config, credentials included (decision §12). */
    config: Record<string, unknown>;
    sources: readonly DataSource[];
    events: DriverEvents;
    log: (message: string) => void;
    onServerDate?: (date: string | undefined, receivedAt: number) => void;
    /** Default `ASK_TIMEOUT_MS`; tests shorten it. */
    askTimeoutMs?: number;
}

const describe = (error: unknown) : string => (error instanceof Error ? error.message : String(error));

/** How long a driver may take to answer a question about its sources (design §6.1). */
export const ASK_TIMEOUT_MS = 10_000;

/** A driver instance, with the questions about its sources (M-2, M-3, M-7) next to the runtime's link. */
export interface DriverHostLink extends DriverLink {
    /** Rejects with the driver's reason, or when it does not answer within `ASK_TIMEOUT_MS`. */
    ask<K extends DriverQuestion['kind']>(question: Extract<DriverQuestion, { kind: K }>) : Promise<DriverAnswers[K]>;
}

/** Where a driver instance keeps its `host.storage`, inside the app's storage. */
export function driverStoragePrefix(connection: string) : string {
    return `driver-data/${connection}`;
}

export function startDriverHost(options: DriverHostOptions) : DriverHostLink {
    const { connection } = options;
    const label = `connection ${connection}`;
    const worker = options.createWorker(`driver-${connection}`);
    const pendingRefreshes = new Map<number, () => void>();
    const pendingCommands = new Map<number, { resolve(done: boolean) : void; reject(error: Error) : void }>();
    const pendingAnswers = new Map<number, { resolve(value: unknown) : void; reject(error: Error) : void }>();
    let nextRefreshId = 1;
    let nextCommandId = 1;
    let nextAskId = 1;
    let disposed = false;

    const post = (message: PageMessage, transfer: ArrayBuffer[] = []) : void => {
        if (!disposed) {
            worker.postMessage(message, transfer);
        }
    };

    const calls = createHostCalls({
        bridge: options.bridge,
        gate: createPermissionGate(options.permissions, options.config),
        storagePrefix: driverStoragePrefix(connection),
        label,
        log: options.log,
        // Copied, not transferred: the bridge may deliver views into a larger frame buffer.
        onSocketEvent: (event) => post(event),
        ...(options.onServerDate ? { onServerDate: options.onServerDate } : {}),
    });

    const call = async (id: number, message: Extract<WorkerMessage, { type: 'call' }>) : Promise<void> => {
        let reply: CallReply;
        let transfer: ArrayBuffer[] = [];
        try {
            const result = await calls.handle(message.call);
            reply = { type: 'reply', id, ok: true, value: result.value };
            transfer = result.transfer;
        } catch (error) {
            reply = { type: 'reply', id, ok: false, error: describe(error) };
        }

        post(reply, transfer);
    };

    worker.onmessage = (event) => {
        const message = event.data;
        switch (message.type) {
            case 'call':
                void call(message.id, message);
                break;
            case 'readings':
                options.events.readings(message.sourceId, message.readings);
                break;
            case 'failed':
                options.log(`${label}, source ${message.sourceId}: ${message.reason}`);
                options.events.failed(message.sourceId, message.reason);
                break;
            case 'refreshed':
                pendingRefreshes.get(message.id)?.();
                pendingRefreshes.delete(message.id);
                break;
            case 'commanded': {
                const pending = pendingCommands.get(message.id);
                pendingCommands.delete(message.id);
                if (message.ok) {
                    pending?.resolve(message.done);
                } else {
                    pending?.reject(new Error(message.error));
                }
                break;
            }
            case 'answer': {
                const pending = pendingAnswers.get(message.id);
                pendingAnswers.delete(message.id);
                if (message.ok) {
                    pending?.resolve(message.value);
                } else {
                    pending?.reject(new Error(message.error));
                }
                break;
            }
            case 'log':
                options.log(`${label}: ${message.message}`);
                break;
        }
    };
    worker.onerror = (event) => options.log(`${label}: worker error: ${event.message ?? 'unknown'}`);

    post({ type: 'init', driver: options.driver, config: options.config });
    for (const source of options.sources) {
        post({ type: 'register', source });
    }

    return {
        refresh(sourceIds) {
            const id = nextRefreshId++;

            return new Promise<void>((resolve) => {
                pendingRefreshes.set(id, resolve);
                post({ type: 'refresh', id, sourceIds: [...sourceIds] });
            });
        },
        command(sourceId, name, arg) {
            if (disposed) {
                return Promise.reject(new Error('the connection was closed'));
            }

            const id = nextCommandId++;

            return new Promise<boolean>((resolve, reject) => {
                pendingCommands.set(id, { resolve, reject });
                post({ type: 'command', id, sourceId, name, ...(arg !== undefined ? { arg } : {}) });
            });
        },
        ask(question) {
            if (disposed) {
                return Promise.reject(new Error('the connection was closed'));
            }

            const id = nextAskId++;
            const timeoutMs = options.askTimeoutMs ?? ASK_TIMEOUT_MS;

            return new Promise((resolve, reject) => {
                const timer = setTimeout(() => {
                    pendingAnswers.delete(id);
                    reject(new Error(`the driver did not answer within ${timeoutMs / 1000} s`));
                }, timeoutMs);
                pendingAnswers.set(id, {
                    resolve: (value) => {
                        clearTimeout(timer);
                        resolve(value as never);
                    },
                    reject: (error) => {
                        clearTimeout(timer);
                        reject(error);
                    },
                });
                post({ type: 'ask', id, question });
            });
        },
        dispose() {
            disposed = true;
            worker.terminate();
            calls.closeAll();
            for (const resolve of pendingRefreshes.values()) {
                resolve();
            }
            pendingRefreshes.clear();
            for (const pending of pendingCommands.values()) {
                pending.reject(new Error('the connection was closed'));
            }
            pendingCommands.clear();
            for (const pending of pendingAnswers.values()) {
                pending.reject(new Error('the connection was closed'));
            }
            pendingAnswers.clear();
        },
    };
}

export type DriverProbeOptions = Omit<DriverHostOptions, 'sources' | 'events'>;

/**
 * A driver started only to be asked about its sources (the driver explorer, the editor's data
 * selection) or to test a configuration: no sources, nothing scheduled. Dispose it when done.
 */
export function startDriverProbe(options: DriverProbeOptions) : DriverHostLink {
    return startDriverHost({ ...options, sources: [], events: { readings: () => {}, failed: () => {} } });
}
